import { useMemo, useRef, useState } from "react";
import { Button, ConfigProvider, Flex, Modal, Tooltip } from "antd";
import { useTranslate } from "@refinedev/core";
import { useAntTheme } from "hooks/useAntTheme";
import { isRTL } from "helpers/utils/";
import { MediaAssetsViewOptions, MediaAssetTabs } from "../types";
import { ImagesTab } from "./StoryboardMenuTabs/ImagesVideosTabs/ImagesTab";
import { VideosTab } from "./StoryboardMenuTabs/ImagesVideosTabs/VideosTab";
import { SegmentedButtons } from "./controls/SegmentedButtons";
import { SlideScriptEditor } from "./StoryboardMenuTabs/ScriptTab/SlideScriptEditor";
import { useTemplateData } from "../hooks/useTemplateData";
import { useSectionVoiceId } from "../hooks/useSectionVoiceId";

const PREVIEW_WIDTH = 320;
const PREVIEW_HEIGHT = 180;

const I18N_KEY =
  "components.templates.TemplatesBoard.storyboardSlidesThumbnails";

type PickedAsset = {
  assetId: string;
  path: string;
  mediaType: "image" | "video";
};

export const MainImageSelectModal = ({
  open,
  onSelect,
  onCancel,
  sectionId,
}: {
  open: boolean;
  onSelect: (
    assetId: string,
    path: string,
    mediaType: "image" | "video",
    script: string
  ) => void;
  onCancel: () => void;
  // Section the new scene will land in, so the pronunciation preview uses its voice
  sectionId?: string;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();

  const {
    mediaAssetsData: { images: uploadedImages },
    projectMediaAssetsData: { images: projectImages },
    templatesData: { storyboard },
  } = useTemplateData();
  const sectionVoiceId = useSectionVoiceId(sectionId);

  // Two-step flow: pick a main image/video, then edit the new scene's script
  // before adding it.
  const [step, setStep] = useState<"image" | "script">("image");
  const [pickedAsset, setPickedAsset] = useState<PickedAsset | null>(null);

  const defaultNarration = t(`${I18N_KEY}.defaultNarration`);
  // Ref mirrors the latest edited script so the "Add scene" click reads it even
  // when the textarea's blur-driven save fires in the same interaction.
  const scriptRef = useRef(defaultNarration);
  const [draftScript, setDraftScript] = useState(defaultNarration);

  const [activeTab, setActiveTab] = useState<MediaAssetTabs>(
    MediaAssetTabs.Images
  );
  // Smart default: prefer the document-extracted images (most relevant to the
  // slide's topic), then the user's uploads, and fall back to stock only when
  // nothing else is available so the modal doesn't open on an empty Pexels grid.
  const [mediaAssetTab, setMediaAssetTab] = useState<MediaAssetsViewOptions>(
    () => {
      if (projectImages.some((i) => i.source_type === "Extracted"))
        return MediaAssetsViewOptions.Document;
      if (uploadedImages.length > 0) return MediaAssetsViewOptions.Uploads;
      return MediaAssetsViewOptions.Stock;
    }
  );

  const mediaAssetTabItems = useMemo(() => {
    let items = Object.values(MediaAssetsViewOptions);
    if (activeTab === MediaAssetTabs.Videos) {
      items = items.filter(
        (item) =>
          item !== MediaAssetsViewOptions.Document &&
          item !== MediaAssetsViewOptions.AIGenerated
      );
    }
    return items.map((value) => ({
      option: value,
      title: t(`components.templates.settings.mediaTab.${value}`, {
        type: activeTab,
      }),
    }));
  }, [activeTab, t]);

  const items = [
    {
      key: MediaAssetTabs.Images,
      label: t("asset_libraries.images"),
    },
    {
      key: MediaAssetTabs.Videos,
      label: t("asset_libraries.videos"),
    },
  ];

  const handlePickAsset = (
    assetId: string,
    path: string,
    mediaType: "image" | "video"
  ) => {
    setPickedAsset({ assetId, path, mediaType });
    setStep("script");
  };

  // `destroyOnClose` only tears down the modal body, not this component, so the
  // two-step flow state survives a close. Reset it whenever the modal closes
  // (add or cancel) so the next open always starts on the image step.
  const resetFlow = () => {
    setStep("image");
    setPickedAsset(null);
    scriptRef.current = defaultNarration;
    setDraftScript(defaultNarration);
  };

  const handleAddScene = () => {
    if (!pickedAsset) return;
    onSelect(
      pickedAsset.assetId,
      pickedAsset.path,
      pickedAsset.mediaType,
      scriptRef.current
    );
    resetFlow();
  };

  const handleCancel = () => {
    resetFlow();
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={t(
        step === "image"
          ? `${I18N_KEY}.mainImagePickerTitle`
          : `${I18N_KEY}.editSceneScriptStep`
      )}
      width={720}
      centered
      styles={{
        content: {
          height: "75vh",
          display: "flex",
          flexDirection: "column",
        },
        body: {
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        },
      }}
      destroyOnClose
      footer={
        step === "script" ? (
          <Flex justify="space-between" align="center">
            <Button onClick={() => setStep("image")}>
              {t(`${I18N_KEY}.backToImageStep`)}
            </Button>
            <Button type="primary" onClick={handleAddScene}>
              {t(`${I18N_KEY}.addSceneConfirm`)}
            </Button>
          </Flex>
        ) : null
      }
    >
      {step === "image" ? (
        <ConfigProvider
          theme={{
            components: {
              Radio: {
                buttonBg: "transparent",
                buttonSolidCheckedBg: theme.colorWhite,
                buttonSolidCheckedColor: theme.colorTextBase,
                colorBorder: "transparent",
                buttonSolidCheckedHoverBg: theme.colorWhite,
                buttonColor: theme.colorTextSecondary,
              },
            },
          }}
        >
          <Flex vertical gap={12} style={{ flex: 1, minHeight: 0 }}>
            <SegmentedButtons
              options={items.map((item) => ({
                label: item.label.toUpperCase(),
                value: item.key,
                tooltip: t(
                  `components.templates.settings.mediaTab.${item.key}`
                ),
              }))}
              value={activeTab}
              onChange={(value) => {
                setActiveTab(value);
                setMediaAssetTab(MediaAssetsViewOptions.Stock);
              }}
              style={{
                width: "80%",
                margin: "0 auto",
                padding: "4px 11px",
              }}
            />
            <SegmentedButtons
              block={activeTab === MediaAssetTabs.Videos}
              value={mediaAssetTab}
              options={mediaAssetTabItems.map((item) => ({
                label: (
                  <Tooltip title={item.title}>
                    {item.option.replaceAll("_", " ").toUpperCase()}
                  </Tooltip>
                ),
                value: item.option,
              }))}
              onChange={(value) => setMediaAssetTab(value)}
            />
            <Flex
              vertical
              style={{ flex: 1, minHeight: 0, overflow: "hidden" }}
            >
              {activeTab === MediaAssetTabs.Images && (
                <ImagesTab
                  previewWidth={PREVIEW_WIDTH}
                  previewHeight={PREVIEW_HEIGHT}
                  mediaAssetTabView={mediaAssetTab}
                  customAddToCanvas={(assetId, path) =>
                    handlePickAsset(assetId, path, "image")
                  }
                  selectionMode
                />
              )}
              {activeTab === MediaAssetTabs.Videos && (
                <VideosTab
                  previewWidth={PREVIEW_WIDTH}
                  previewHeight={PREVIEW_HEIGHT}
                  mediaAssetTabView={mediaAssetTab}
                  customAddToCanvas={(assetId, path) =>
                    handlePickAsset(assetId, path, "video")
                  }
                  selectionMode
                />
              )}
            </Flex>
          </Flex>
        </ConfigProvider>
      ) : (
        <Flex vertical style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <SlideScriptEditor
            script={draftScript}
            index={0}
            isRTL={isRTL(storyboard?.language ?? "")}
            language={storyboard?.language}
            onSave={(content) => {
              scriptRef.current = content;
              setDraftScript(content);
            }}
            sectionId={sectionId}
            voiceId={sectionVoiceId}
          />
        </Flex>
      )}
    </Modal>
  );
};
